<template>
  <!--
    Komponent wyboru zakresu dat z pickerem.
    Umożliwia użytkownikowi ustawienie daty początkowej i końcowej.
    Wyświetla tytuł oraz dwa pickery dla "Od" i "Do".
  -->
  <div class="date-range">
    <!--
      Nagłówek sekcji z dynamiczną klasą koloru, jeśli są błędy wymaganych pól.
    -->
    <h3
      :class="{
        'text-danger':
          this.store.error &&
          (this.store.requireFields.includes('dateTimeStart') ||
            this.store.requireFields.includes('dateTimeEnd')),
      }"
      class="margin-0"
    >
      {{ t('date-range').toUpperCase() }}
    </h3>
    <!--
      Tabela zawierająca dwa rzędy pickerów:
      - w pierwszym rzędzie picker dla daty początkowej
      - w drugim rzędzie picker dla daty końcowej
    -->
    <table class="table">
      <thead>
        <tr>
          <th class="sign" scope="col">&gt;</th>
          <th scope="col">{{ t('date') }}</th>
          <th class="hour center-aligned" scope="col">{{ t('hour') }}</th>
          <th scope="col"></th>
        </tr>
      </thead>
      <tbody>
        <!-- Picker daty początkowej -->
        <tr>
          <td colspan="4">
            <VueDatePicker
              v-model="fromDate"
              time-picker-inline
              :format="fromFormat"
              :month-change-on-scroll="false"
              locale="pl"
              cancelText="Anuluj"
              selectText="Wybierz"
              inputClassName="daterange-input"
              :disabled="store.disabledFields?.dateFrom"
            ></VueDatePicker>
          </td>
        </tr>
        <!-- Picker daty końcowej -->
        <tr>
          <td colspan="4">
            <VueDatePicker
              v-model="toDate"
              time-picker-inline
              :format="toFormat"
              :month-change-on-scroll="false"
              locale="pl"
              cancelText="Anuluj"
              selectText="Wybierz"
              inputClassName="daterange-input"
              :disabled="store.disabledFields?.dateTo"
            ></VueDatePicker>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script>
import { watch, ref } from "vue";
import VueDatePicker from "@vuepic/vue-datepicker";
import "@vuepic/vue-datepicker/dist/main.css";

export default {
  name: "DateRange",  // Nazwa komponentu
  props: {
    store: { required: true },       // Pinia store z filtrami dat
    dateTimeStart: { required: false },
    dateTimeEnd: { required: false },
  },
  components: { VueDatePicker },
  setup(props) {
    // inicjalizacja wartości dla pickerów na podstawie store
    const fromDate = ref(
      props.store.filters.dateTimeStart
        ? props.store.filters.dateTimeStart
        : null
    );
    const toDate = ref(
      props.store.filters.dateTimeEnd ? props.store.filters.dateTimeEnd : null
    );

    // Format wyświetlanej daty początkowej
    const fromFormat = (date) => {
      return `Od ${(date.getDate()<10?"0":"")+date.getDate()}.${
        (date.getMonth()+1<10?"0":"")+(date.getMonth()+1)
      }.${date.getFullYear()},  ${date.getHours()}:${
        (date.getMinutes()<10?"0":"")+date.getMinutes()
      }`;
    };
    // Format wyświetlanej daty końcowej
    const toFormat = (date) => {
      return `Do ${(date.getDate()<10?"0":"")+date.getDate()}.${
        (date.getMonth()+1<10?"0":"")+(date.getMonth()+1)
      }.${date.getFullYear()},  ${date.getHours()}:${
        (date.getMinutes()<10?"0":"")+date.getMinutes()
      }`;
    };

    // Helper do zapisu wartości do store
    const saveValue = (fieldName, text) => {
      props.store.addItem(fieldName, text);
    };

    // Watcher monitorujący zmiany obu pickerów
    watch(
      [fromDate, toDate],
      ([newFromDate, newToDate], [prevFromDate, prevToDate]) => {
        // Obsługa zmiany daty początkowej
        if (newFromDate !== prevFromDate) {
          if (fromDate.value === null) {
            // usunięcie filtra z store, jeśli wyczyszczono
            delete props.store.filters.dateTimeStart;
          } else {
            // zapis do store i korekta daty końcowej, jeśli wcześniejsza
            saveValue(
              "dateTimeStart",
              new Date(fromDate.value).toISOString()
            );
            if (fromDate.value > toDate.value) {
              toDate.value = new Date(
                new Date(fromDate.value).setDate(
                  new Date(fromDate.value).getDate() + 1
                )
              );
            }
          }
        }
        // Obsługa zmiany daty końcowej
        if (newToDate !== prevToDate) {
          if (toDate.value === null) {
            delete props.store.filters.dateTimeEnd;
          } else {
            saveValue("dateTimeEnd", new Date(toDate.value).toISOString());
            if (toDate.value < fromDate.value) {
              fromDate.value = new Date(
                new Date(toDate.value).setDate(
                  new Date(toDate.value).getDate() - 1
                )
              );
            }
          }
        }
      }
    );

    return { fromDate, toDate, fromFormat, toFormat };
  },
  watch: {
    // Synchronizacja propów z lokalnym refem
    dateTimeStart(newVal) {
      this.fromDate = newVal;
    },
    dateTimeEnd(newVal) {
      this.toDate = newVal;
    },
  },
  created() {
    // Watchery na zmiany store, aby reagować na usunięcie wartości
    this.$watch(
      () => this.store.filters.dateTimeStart,
      (val) => { if(!val) this.fromDate = null; }
    );
    this.$watch(
      () => this.store.filters.dateTimeEnd,
      (val) => { if(!val) this.toDate = null; }
    );
  },
};
</script>

<style scoped>
/* Styl inputów datepicker */
.daterange-input {
  font-size: 14px;
}
/* Styl kontenera komponentu */
.date-range {
  width: 270px;
  min-height: 140px;
}
.date-range h3 {
  padding: 0.85rem 1rem 0.9rem 1rem;
  width: 100%;
  border-right: 1px solid #bfbfbf;
  border-bottom: 1px solid #bfbfbf;
  border-top: 1px solid #bfbfbf;
}
.date-range table {
  width: 100%;
  border-collapse: collapse;
  text-align: center;
  background-color: white;
  table-layout: fixed;
}
.date-range th {
  padding: 0.5rem;
  font-weight: normal;
  font-size: 12px;
  text-align: center;
  vertical-align: middle;
  height: 43px;
  border-bottom: 1px solid #bfbfbf;
}
.date-range th:last-child {
  border-right: 1px solid #bfbfbf;
}
.date-range td {
  padding: 6.5px 7px;
  background-color: #f5f4f2;
  border-right: 1px solid #bfbfbf;
  border-bottom: 1px solid #bfbfbf;
}
.sign {
  color: #eaeaea;
  font-weight: 1000;
  font-size: 15px;
}
</style>
